import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { SimulationConfig, Ball, TrailCircle, CollisionEvent, BlastEvent } from '@/types';
import { PerformanceTelemetry } from '@/services/PerformanceTelemetry';
import { AUDIO } from '@/services/AudioService';
import { Activity, Cpu, Database, Cpu as Gpu, Volume2, Video } from 'lucide-react';

interface DiagnosticsOverlayProps {
  ballsRef: React.MutableRefObject<Ball[]>;
  trailCirclesRef: React.MutableRefObject<TrailCircle[]>;
  collisionEventsRef: React.MutableRefObject<CollisionEvent[]>;
  blastEventsRef: React.MutableRefObject<BlastEvent[]>;
  ballPoolRef: React.MutableRefObject<any>;
  trailCirclePoolRef: React.MutableRefObject<any>;
  collisionEventPoolRef: React.MutableRefObject<any>;
  blastEventPoolRef: React.MutableRefObject<any>;
  config: SimulationConfig;
}

export const DiagnosticsOverlay: React.FC<DiagnosticsOverlayProps> = ({
  ballsRef,
  trailCirclesRef,
  collisionEventsRef,
  blastEventsRef,
  ballPoolRef,
  trailCirclePoolRef,
  collisionEventPoolRef,
  blastEventPoolRef,
  config
}) => {
  const [metrics, setMetrics] = useState({
    fps: 0,
    cpuTime: 0,
    gpuTime: 0,
    gpuHardwareTime: 0,
    gpuQuerySupported: false,
    vertexCountUploaded: 0,
    indexCountDrawn: 0,
    bytesUploaded: 0,
    isWebGPUActive: false,
    webglDrawCalls: 0,
    webgpuDrawCalls: 0,
    canvas2DDraws: 0,
    approximatedVRAM: 0,
    webgpuTier: 0,
    vramDetails: {
      vertexBuffers: 0,
      indexBuffers: 0,
      uniformBuffers: 0,
      storageBuffers: 0,
      texturesAndFB: 0,
      queryBuffers: 0
    },
    workerRTT: 0,
    workerActive: false,
    workerMode: 'NONE',
    
    // Pools & Actives
    ballsActive: 0,
    ballsPooled: 0,
    trailsActive: 0,
    trailsPooled: 0,
    collisionsActive: 0,
    collisionsPooled: 0,
    blastsActive: 0,
    blastsPooled: 0,

    // Audio
    audioVoices: 0,

    // Off-thread Video Export
    recorderActive: false,
    recorderCompositingTime: 0,
    recorderBacklog: 0,
    recorderFramesCompleted: 0
  });

  useEffect(() => {
    // Decoupled polling interval at 120ms to avoid any React main thread interference
    const interval = setInterval(() => {
      setMetrics({
        fps: PerformanceTelemetry.fps || 60,
        cpuTime: PerformanceTelemetry.cpuTime,
        gpuTime: PerformanceTelemetry.gpuTime,
        gpuHardwareTime: PerformanceTelemetry.gpuHardwareTime,
        gpuQuerySupported: PerformanceTelemetry.gpuQuerySupported,
        vertexCountUploaded: PerformanceTelemetry.vertexCountUploaded,
        indexCountDrawn: PerformanceTelemetry.indexCountDrawn,
        bytesUploaded: PerformanceTelemetry.bytesUploaded,
        isWebGPUActive: PerformanceTelemetry.isWebGPUActive,
        webglDrawCalls: PerformanceTelemetry.webglDrawCalls,
        webgpuDrawCalls: PerformanceTelemetry.webgpuDrawCalls,
        canvas2DDraws: PerformanceTelemetry.canvas2DDraws,
        approximatedVRAM: PerformanceTelemetry.approximatedVRAM,
        webgpuTier: PerformanceTelemetry.webgpuTier,
        vramDetails: { ...PerformanceTelemetry.vramDetails },
        workerRTT: PerformanceTelemetry.workerRTT,
        workerActive: PerformanceTelemetry.workerActive,
        workerMode: PerformanceTelemetry.workerMode,
        
        ballsActive: ballsRef.current ? ballsRef.current.length : 0,
        ballsPooled: ballPoolRef.current ? ballPoolRef.current.getAvailableCount() : 0,
        
        trailsActive: trailCirclesRef.current ? trailCirclesRef.current.length : 0,
        trailsPooled: trailCirclePoolRef.current ? trailCirclePoolRef.current.getAvailableCount() : 0,
        
        collisionsActive: collisionEventsRef.current ? collisionEventsRef.current.length : 0,
        collisionsPooled: collisionEventPoolRef.current ? collisionEventPoolRef.current.getAvailableCount() : 0,
        
        blastsActive: blastEventsRef.current ? blastEventsRef.current.length : 0,
        blastsPooled: blastEventPoolRef.current ? blastEventPoolRef.current.getAvailableCount() : 0,

        audioVoices: (AUDIO as any).getActiveVoices ? (AUDIO as any).getActiveVoices() : 0,

        recorderActive: PerformanceTelemetry.recorderActive,
        recorderCompositingTime: PerformanceTelemetry.recorderCompositingTime,
        recorderBacklog: PerformanceTelemetry.recorderBacklog,
        recorderFramesCompleted: PerformanceTelemetry.recorderFramesCompleted
      });
    }, 120);

    return () => clearInterval(interval);
  }, [
    ballsRef,
    trailCirclesRef,
    collisionEventsRef,
    blastEventsRef,
    ballPoolRef,
    trailCirclePoolRef,
    collisionEventPoolRef,
    blastEventPoolRef
  ]);

  if (!config.showDiagnosticsOverlay) return null;

  // Frame budget timing calculations
  const expectedFps = config.simulationFPS || 60;
  const frameBudgetMs = 1000 / expectedUps(expectedFps);
  const totalOccupiedMs = metrics.cpuTime + metrics.gpuTime;
  const idleMs = Math.max(0, frameBudgetMs - totalOccupiedMs);
  const cpuPercent = Math.min(100, (metrics.cpuTime / frameBudgetMs) * 100);
  const gpuPercent = Math.min(100, (metrics.gpuTime / frameBudgetMs) * 100);
  const idlePercent = Math.max(0, 100 - cpuPercent - gpuPercent);

  function expectedUps(fpsVal: number) {
    return fpsVal > 0 ? fpsVal : 60;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 0.93, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className={`fixed top-18 right-4 z-[9999] w-[340px] font-mono shadow-2xl overflow-hidden text-xs select-none pointer-events-auto rounded-2xl border ${
          config.darkMode 
            ? 'background-slate-900/90 text-zinc-100 border-white/10 shadow-black/80' 
            : 'bg-zinc-50 border-zinc-200 text-zinc-900 shadow-zinc-900/10'
        }`}
        id="perf-diagnostics-overlay"
      >
        {/* Panel Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b ${
          config.darkMode ? 'border-white/10 bg-white/5' : 'border-zinc-200 bg-zinc-100'
        }`}>
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="text-[10px] font-black uppercase tracking-widest leading-none">
              Engine Telemetry v10.0
            </span>
          </div>
          <span className={`text-[8px] font-black px-1.5 py-0.5 rounded uppercase ${
            metrics.fps >= expectedFps - 5 
              ? 'bg-emerald-500/10 text-emerald-400' 
              : 'bg-amber-500/10 text-amber-500'
          }`}>
            {metrics.fps} FPS
          </span>
        </div>

        {/* Diagnostic Sections */}
        <div className="p-4 space-y-4">
          
          {/* Section 1: Frame Latency Budget & Loop Timing */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between opacity-50 text-[9px] uppercase tracking-wider font-extrabold">
              <span className="flex items-center gap-1"><Cpu className="w-3.5 h-3.5" /> Frame Loop Latency</span>
              <span>Budget: {frameBudgetMs.toFixed(1)}ms</span>
            </div>
            
            {/* Horizontal Timeline Bar Block */}
            <div className="h-2 w-full rounded overflow-hidden flex bg-zinc-500/10 border border-zinc-500/5">
              <div 
                style={{ width: `${cpuPercent}%` }} 
                className="bg-sky-500 h-full transition-all duration-100" 
                title={`CPU Math: ${metrics.cpuTime.toFixed(2)}ms`}
              />
              <div 
                style={{ width: `${gpuPercent}%` }} 
                className="bg-indigo-500 h-full transition-all duration-100" 
                title={`GPU Commands: ${metrics.gpuTime.toFixed(2)}ms`}
              />
              <div 
                style={{ width: `${idlePercent}%` }} 
                className="bg-emerald-500 h-full transition-all duration-100" 
                title={`Idle Headroom: ${idleMs.toFixed(2)}ms`}
              />
            </div>

            <div className="grid grid-cols-3 gap-1 uppercase text-[8px] opacity-80 text-center font-bold">
              <div className="text-sky-400">CPU {metrics.cpuTime.toFixed(2)}ms</div>
              <div className="text-indigo-400">GPU {metrics.gpuTime.toFixed(2)}ms</div>
              <div className="text-emerald-400">Idle {idleMs.toFixed(2)}ms</div>
            </div>
          </div>

          {/* Section 2: Thread Pool & Worker Communication RTT */}
          <div className="space-y-1.5 pt-2 border-t border-zinc-500/5">
            <div className="flex items-center justify-between opacity-50 text-[9px] uppercase tracking-wider font-extrabold">
              <span className="flex items-center gap-1"><Activity className="w-3.5 h-3.5" /> Off-Thread Web Worker</span>
              <span className={metrics.workerActive ? 'text-emerald-400' : 'text-zinc-500'}>
                {metrics.workerActive ? 'ACTIVE' : 'NONE'}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className={`p-2 rounded-lg border ${
                config.darkMode ? 'bg-white/5 border-white/5' : 'bg-white border-zinc-200'
              }`}>
                <div className="opacity-40 text-[7px] font-black uppercase tracking-widest text-[9px]">SAB Channel</div>
                <div className="text-xs font-black mt-0.5 uppercase">
                  {metrics.workerMode === 'SAB' ? 'SAB (Shared)' : metrics.workerMode === 'TRANSFERABLE' ? 'Double Buffer' : 'Disabled'}
                </div>
              </div>
              <div className={`p-2 rounded-lg border ${
                config.darkMode ? 'bg-white/5 border-white/5' : 'bg-white border-zinc-200'
              }`}>
                <div className="opacity-40 text-[7px] font-black uppercase tracking-widest text-[9px]">Thread RTT</div>
                <div className="text-xs font-black mt-0.5">
                  {metrics.workerActive ? `${metrics.workerRTT.toFixed(2)} ms` : 'N/A'}
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Graphics Draw Calls, Buffers & VRAM Allocation */}
          <div className="space-y-1.5 pt-2 border-t border-zinc-500/5">
            <div className="flex items-center justify-between opacity-50 text-[9px] uppercase tracking-wider font-extrabold">
              <span className="flex items-center gap-1"><Gpu className="w-3.5 h-3.5" /> Graphics pipeline</span>
              <div className="flex items-center gap-1.5">
                {metrics.isWebGPUActive && metrics.webgpuTier > 0 && (
                  <span className={`px-1 py-0.5 rounded text-[7px] font-bold uppercase ${
                    metrics.webgpuTier === 3 
                      ? 'bg-emerald-500/10 text-emerald-400' 
                      : metrics.webgpuTier === 2 
                        ? 'bg-cyan-500/10 text-cyan-400' 
                        : 'bg-amber-500/10 text-amber-500'
                  }`}>
                    {metrics.webgpuTier === 3 ? 'Tier 3 (Discrete)' : metrics.webgpuTier === 2 ? 'Tier 2 (Unified)' : 'Tier 1 (Soft)'}
                  </span>
                )}
                <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wide ${
                  metrics.isWebGPUActive 
                    ? 'bg-indigo-500/10 text-indigo-400' 
                    : 'bg-amber-500/10 text-amber-500'
                }`}>
                  {metrics.isWebGPUActive ? 'WebGPU Active' : 'WebGL 2.0'}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className={`p-2 rounded-lg border ${
                config.darkMode ? 'bg-white/5 border-white/5' : 'bg-white border-zinc-200'
              }`}>
                <div className="opacity-40 text-[7px] font-black uppercase tracking-widest leading-normal">Draw Operations</div>
                <div className="text-[11px] font-black mt-0.5">
                  {metrics.isWebGPUActive 
                    ? `${metrics.webgpuDrawCalls} Pass / ${metrics.canvas2DDraws} 2D` 
                    : `${metrics.webglDrawCalls} Draw / ${metrics.canvas2DDraws} 2D`}
                </div>
              </div>
              <div className={`p-2 rounded-lg border ${
                config.darkMode ? 'bg-white/5 border-white/5' : 'bg-white border-zinc-200'
              }`}>
                <div className="opacity-40 text-[7px] font-black uppercase tracking-widest leading-normal">VRAM Allocated</div>
                <div className="text-[11px] font-black mt-0.5 text-indigo-400">
                  {metrics.approximatedVRAM > 0 ? `${metrics.approximatedVRAM.toFixed(2)} MB` : '0.00 MB'}
                </div>
              </div>
            </div>

            {/* Hardware timestamp metrics if supported */}
            {metrics.isWebGPUActive && (
              <div className="mt-1 space-y-1 bg-zinc-500/5 p-2 rounded-lg">
                <div className="flex justify-between items-center text-[7px] uppercase font-black tracking-wider text-zinc-400">
                  <span>GPU TIMING QUERIES</span>
                  <span>{metrics.gpuQuerySupported ? 'HARDWARE ACTIVE' : 'PREPARATION TIMING'}</span>
                </div>
                <div className="flex justify-between items-center text-[10px] mt-0.5">
                  <span className="opacity-60">CPU Pass Prepare</span>
                  <span className="font-extrabold text-indigo-400">{metrics.gpuTime.toFixed(2)} ms</span>
                </div>
                {metrics.gpuQuerySupported && (
                  <div className="flex justify-between items-center text-[10px]">
                    <span className="opacity-60">GPU Hardware Execution</span>
                    <span className="font-extrabold text-emerald-400">{metrics.gpuHardwareTime.toFixed(3)} ms</span>
                  </div>
                )}
              </div>
            )}

            {/* Buffer Uploads Tracker */}
            <div className="mt-1 space-y-0.5 bg-zinc-500/5 p-2 rounded-lg text-[10px]">
              <div className="flex justify-between items-center text-[7px] uppercase font-black tracking-wider text-zinc-400">
                <span>BUFFER BANDWIDTH</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="opacity-60">Vertices Uploaded</span>
                <span className="font-semibold">{metrics.vertexCountUploaded.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="opacity-60">Indices Drawn</span>
                <span className="font-semibold">{metrics.indexCountDrawn.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="opacity-60">Frame Data Sent</span>
                <span className="font-semibold text-sky-400">{(metrics.bytesUploaded / 1024).toFixed(1)} KB</span>
              </div>
            </div>

            {/* Detailed VRAM details breakdown */}
            <div className="mt-1 space-y-0.5 bg-zinc-500/5 p-2 rounded-lg text-[10px]">
              <div className="flex justify-between items-center text-[7px] uppercase font-black tracking-wider text-zinc-400">
                <span>VRAM BLOCK DETAILS</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="opacity-60">Vertex Buffers</span>
                <span className="font-semibold">{(metrics.vramDetails.vertexBuffers / 1024).toFixed(1)} KB</span>
              </div>
              {metrics.isWebGPUActive && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="opacity-60">Uniform Buffers</span>
                    <span className="font-semibold">{metrics.vramDetails.uniformBuffers} bytes</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="opacity-60">Texture Swapchain</span>
                    <span className="font-semibold text-zinc-300">{(metrics.vramDetails.texturesAndFB / (1024 * 1024)).toFixed(1)} MB</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Section 4: Object Pool Memory Utilization */}
          <div className="space-y-1.5 pt-2 border-t border-zinc-500/5">
            <div className="flex items-center justify-between opacity-50 text-[9px] uppercase tracking-wider font-extrabold text-[9px]">
              <span className="flex items-center gap-1"><Database className="w-3.5 h-3.5" /> Memory Buffer Pools</span>
              <span>Available / Active</span>
            </div>

            <div className="space-y-1 text-[10px] leading-relaxed">
              <div className="flex justify-between items-center bg-zinc-500/5 px-2 py-1 rounded">
                <span className="opacity-60">Spheres Pool</span>
                <span className="font-extrabold">
                  {metrics.ballsPooled} <span className="opacity-40">/</span> {metrics.ballsActive}
                </span>
              </div>
              <div className="flex justify-between items-center bg-zinc-500/5 px-2 py-1 rounded">
                <span className="opacity-60">Trails Pool</span>
                <span className="font-extrabold">
                  {metrics.trailsPooled} <span className="opacity-40">/</span> {metrics.trailsActive}
                </span>
              </div>
              <div className="flex justify-between items-center bg-zinc-500/5 px-2 py-1 rounded">
                <span className="opacity-60">Collision FX Pool</span>
                <span className="font-extrabold">
                  {metrics.collisionsPooled} <span className="opacity-40">/</span> {metrics.collisionsActive}
                </span>
              </div>
              <div className="flex justify-between items-center bg-zinc-500/5 px-2 py-1 rounded">
                <span className="opacity-60">Shrapnel Blasts Pool</span>
                <span className="font-extrabold">
                  {metrics.blastsPooled} <span className="opacity-40">/</span> {metrics.blastsActive}
                </span>
              </div>
            </div>
          </div>

          {/* Section 5: Synthesizer Active Voices */}
          <div className="space-y-1.5 pt-2 border-t border-zinc-500/5">
            <div className="flex justify-between items-center opacity-50 text-[9px] uppercase tracking-wider font-extrabold">
              <span className="flex items-center gap-1"><Volume2 className="w-3.5 h-3.5" /> Audio Engine FM Synthesis</span>
              <span>{metrics.audioVoices} Voices Active</span>
            </div>
          </div>

          {/* Section 6: Off-Thread Exporter Telemetry */}
          {metrics.recorderActive && (
            <div className="space-y-1.5 pt-2 border-t border-zinc-500/5 text-[10px]">
              <div className="flex justify-between items-center opacity-50 text-[9px] uppercase tracking-wider font-extrabold">
                <span className="flex items-center gap-1"><Video className="w-3.5 h-3.5 text-rose-400 animate-pulse" /> Off-Thread Exporter</span>
                <span className="text-rose-400 font-extrabold animate-pulse leading-none">RECORDING</span>
              </div>
              <div className="space-y-1 text-[10px] leading-relaxed">
                <div className="flex justify-between items-center bg-zinc-500/5 px-2 py-1 rounded">
                  <span className="opacity-60">Encoder Queue</span>
                  <span className="font-extrabold text-amber-400">
                    {metrics.recorderBacklog} frames
                  </span>
                </div>
                <div className="flex justify-between items-center bg-zinc-500/5 px-2 py-1 rounded">
                  <span className="opacity-60">Compositing Overhead</span>
                  <span className="font-extrabold text-teal-400">
                    {metrics.recorderCompositingTime.toFixed(1)} ms
                  </span>
                </div>
                <div className="flex justify-between items-center bg-zinc-500/5 px-2 py-1 rounded">
                  <span className="opacity-60">Compiled Frames</span>
                  <span className="font-extrabold">
                    {metrics.recorderFramesCompleted}
                  </span>
                </div>
              </div>
            </div>
          )}

        </div>
      </motion.div>
    </AnimatePresence>
  );
};
